import React, { useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

export default function AuthPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const clearError = () => setError('');

  const getErrorMessage = (code) => {
    const messages = {
      'auth/user-not-found':        'Kein Konto mit dieser E-Mail gefunden.',
      'auth/wrong-password':        'Falsches Passwort.',
      'auth/invalid-credential':    'E-Mail oder Passwort falsch.',
      'auth/email-already-in-use':  'Diese E-Mail wird bereits verwendet.',
      'auth/weak-password':         'Passwort muss mindestens 6 Zeichen lang sein.',
      'auth/invalid-email':         'Ungültige E-Mail-Adresse.',
      'auth/too-many-requests':     'Zu viele Versuche. Bitte kurz warten.',
      'auth/popup-closed-by-user':  'Anmeldung abgebrochen.',
      'auth/network-request-failed':'Netzwerkfehler. Bitte Verbindung prüfen.',
    };
    return messages[code] || 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else if (mode === 'register') {
        await createUserWithEmailAndPassword(auth, email, password);
      } else if (mode === 'reset') {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
      }
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    clearError();
    setGoogleLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-100 rounded-full opacity-40 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-50 rounded-full opacity-60 blur-3xl" />
      </div>

      {/* Card */}
      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center shadow-md">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 2L13 8L3 14V2Z" fill="white" />
            </svg>
          </div>
          <span className="text-xl font-semibold text-gray-900 tracking-tight">FrameIt</span>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          {/* Heading */}
          <div className="mb-6">
            <h1 className="text-lg font-semibold text-gray-900">
              {mode === 'login'    && 'Willkommen zurück'}
              {mode === 'register' && 'Konto erstellen'}
              {mode === 'reset'    && 'Passwort zurücksetzen'}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {mode === 'login'    && 'Melde dich mit deinem Konto an.'}
              {mode === 'register' && 'Starte mit FrameIt – kostenlos.'}
              {mode === 'reset'    && 'Wir schicken dir einen Reset-Link.'}
            </p>
          </div>

          {/* Reset sent confirmation */}
          {resetSent && mode === 'reset' ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10L8 14L16 6" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-800 mb-1">E-Mail gesendet!</p>
              <p className="text-xs text-gray-400">Bitte prüfe dein Postfach für <span className="font-medium">{email}</span></p>
              <button
                onClick={() => { setMode('login'); setResetSent(false); }}
                className="mt-4 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Zurück zur Anmeldung
              </button>
            </div>
          ) : (
            <>
              {/* Google Button */}
              {mode !== 'reset' && (
                <>
                  <button
                    onClick={handleGoogle}
                    disabled={googleLoading}
                    className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 mb-5"
                  >
                    {googleLoading ? (
                      <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                        <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                        <path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                      </svg>
                    )}
                    Mit Google fortfahren
                  </button>

                  <div className="flex items-center gap-3 mb-5">
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="text-xs text-gray-400">oder</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                </>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">E-Mail</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); clearError(); }}
                    required
                    placeholder="du@beispiel.de"
                    className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all placeholder-gray-300"
                  />
                </div>

                {mode !== 'reset' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Passwort</label>
                    <input
                      type="password"
                      value={password}
                      onChange={e => { setPassword(e.target.value); clearError(); }}
                      required
                      placeholder="••••••••"
                      minLength={6}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all placeholder-gray-300"
                    />
                  </div>
                )}

                {mode === 'register' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Passwort bestätigen</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={e => { setConfirmPassword(e.target.value); clearError(); }}
                      required
                      placeholder="••••••••"
                      className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 transition-all placeholder-gray-300"
                    />
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl px-3.5 py-3">
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" className="mt-0.5 shrink-0">
                      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3"/>
                      <path d="M7 4V7.5M7 9.5V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    {error}
                  </div>
                )}

                {/* Forgot password link */}
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setMode('reset'); clearError(); }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 text-right self-end -mt-1"
                  >
                    Passwort vergessen?
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 mt-1"
                >
                  {loading && (
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                  )}
                  {mode === 'login'    && 'Anmelden'}
                  {mode === 'register' && 'Konto erstellen'}
                  {mode === 'reset'    && 'Reset-Link senden'}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Mode switch */}
        <p className="text-center text-xs text-gray-400 mt-5">
          {mode === 'login' && (
            <>Noch kein Konto?{' '}
              <button onClick={() => { setMode('register'); clearError(); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                Registrieren
              </button>
            </>
          )}
          {mode === 'register' && (
            <>Bereits registriert?{' '}
              <button onClick={() => { setMode('login'); clearError(); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                Anmelden
              </button>
            </>
          )}
          {mode === 'reset' && !resetSent && (
            <button onClick={() => { setMode('login'); clearError(); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
              ← Zurück
            </button>
          )}
        </p>
      </div>
    </div>
  );
}
